import { ResponsiveBar } from '@nivo/bar';
import { ResponsiveLine } from '@nivo/line';
import classNames from 'classnames';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ButtonSwitchMulti from '../../components/actions/ButtonSwitchMulti';
import DropdownSelector from '../../components/actions/DropdownSelector';
import * as Icon from '../../components/icons';
import { Stat } from '../../components/stats/Stat';
import { StatListItem } from '../../components/stats/StatListItem';
import ContainerChart from '../../simulation/panels/ContainerChart';
import {
  FatigueMetrics,
  LaborMetrics,
  ProductivityMetrics,
} from '../types/metrics';

interface WarehouseDashboardLabourProps {
  laborMetrics: LaborMetrics[];
  productivityMetrics: ProductivityMetrics;
  fatigueMetrics: FatigueMetrics[];
  timeRange: string;
}

const ShiftUtilizationCard: React.FC<{ metrics: LaborMetrics }> = ({
  metrics,
}) => {
  const { t } = useTranslation('app');

  return (
    <Stat
      hasHelper
      isPreview
      title={t('Shift Utilization')}
      value={metrics.utilization.toString()}
      unitOfMeasure="%"
      icon={Icon.Clock}
    >
      <StatListItem
        title={t('Active Time')}
        value={metrics.activeTime}
        unitOfMeasure="h"
      />
      <StatListItem
        title={t('Break Time')}
        value={metrics.breakTime}
        unitOfMeasure="h"
      />
      <StatListItem
        title={t('Productivity')}
        value={metrics.productivity}
        unitOfMeasure="%"
      />
    </Stat>
  );
};

const FTETrackingCard: React.FC<{ metrics: LaborMetrics }> = ({ metrics }) => {
  const { t } = useTranslation('app');

  return (
    <Stat
      hasHelper
      isPreview
      title={t('FTE Tracking')}
      value={metrics.totalFTE.toString()}
      icon={Icon.Users}
    >
      <StatListItem title={t('Planned FTE')} value={metrics.plannedFTE} />
      <StatListItem
        title={t('Actual Hours')}
        value={metrics.actualHours}
        unitOfMeasure="h"
      />
      <StatListItem
        title={t('Planned Hours')}
        value={metrics.plannedHours}
        unitOfMeasure="h"
      />
    </Stat>
  );
};

const ProductivityCard: React.FC<{ metrics: ProductivityMetrics }> = ({
  metrics,
}) => {
  const { t } = useTranslation('app');

  return (
    <Stat
      hasHelper
      isPreview
      title={t('Productivity Metrics')}
      value={metrics.efficiency.toString()}
      unitOfMeasure="%"
      icon={Icon.ChartLineUp}
    >
      <StatListItem title={t('Units/Hour')} value={metrics.unitsPerHour} />
      <StatListItem title={t('Lines/Hour')} value={metrics.linesPerHour} />
      <StatListItem title={t('Orders/Hour')} value={metrics.ordersPerHour} />
      <StatListItem
        title={t('Cost/Unit')}
        value={metrics.costPerUnit}
        unitOfMeasure="$"
        isUnitPrefix={true}
      />
    </Stat>
  );
};

const ProductivityBarChart: React.FC<{
  laborMetrics: LaborMetrics[];
  productivityMetrics: ProductivityMetrics;
}> = ({ laborMetrics, productivityMetrics }) => {
  const { t } = useTranslation('app');

  const barData = [
    {
      metric: t('Utilization'),
      value: laborMetrics[0].utilization,
      color: 'hsl(120, 70%, 50%)',
    },
    {
      metric: t('Productivity'),
      value: laborMetrics[0].productivity,
      color: 'hsl(180, 70%, 50%)',
    },
    {
      metric: t('Efficiency'),
      value: productivityMetrics.efficiency,
      color: 'hsl(60, 70%, 50%)',
    },
  ];

  return (
    <ContainerChart containerHeight={200}>
      <ResponsiveBar
        data={barData}
        keys={['value']}
        indexBy="metric"
        margin={{ top: 20, right: 30, bottom: 50, left: 60 }}
        padding={0.3}
        valueScale={{ type: 'linear' }}
        indexScale={{ type: 'band', round: true }}
        colors={{ scheme: 'set2' }}
        colorBy="indexValue"
        borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
        axisTop={null}
        axisRight={null}
        axisBottom={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: t('Metrics'),
          legendPosition: 'middle',
          legendOffset: 32,
        }}
        axisLeft={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: t('Percentage (%)'),
          legendPosition: 'middle',
          legendOffset: -40,
        }}
        labelSkipWidth={12}
        labelSkipHeight={12}
        labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
        legends={[]}
        animate={true}
        theme={{
          axis: {
            ticks: {
              text: {
                fill: '#cccccc',
              },
            },
            legend: {
              text: {
                fill: '#cccccc',
              },
            },
          },
          grid: {
            line: {
              stroke: '#555555',
              strokeWidth: 1,
            },
          },
          legends: {
            text: {
              fill: '#cccccc',
            },
          },
          tooltip: {
            container: {
              background: '#333333',
              color: '#ffffff',
            },
          },
        }}
      />
    </ContainerChart>
  );
};

const WorkforceTimeAnalysis: React.FC<{
  fatigueMetrics: FatigueMetrics[];
  showDetails: boolean;
}> = ({ fatigueMetrics, showDetails }) => {
  const { t } = useTranslation('app');

  // Generate a time series for productivity trends
  const productivityData = [
    {
      id: 'productivity',
      color: 'hsl(120, 70%, 50%)',
      data: fatigueMetrics
        .sort(
          (a, b) =>
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
        )
        .map(metric => ({
          x: new Date(metric.timestamp).getHours() + ':00',
          y: metric.productivityTrend,
        })),
    },
  ];

  // Create fatigue indicators from metrics
  const fatigueData = fatigueMetrics
    .sort(
      (a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
    )
    .map(metric => ({
      time: new Date(metric.timestamp).getHours() + ':00',
      distance: metric.avgDistance,
      weight: metric.avgWeight,
      temperature: metric.temperature,
      restNeeded: metric.restNeeded,
    }));

  if (fatigueMetrics.length === 0) {
    return (
      <div className="p-4 text-center">No productivity data available</div>
    );
  }

  return (
    <div className="flex w-full flex-1 flex-col">
      <ContainerChart containerHeight={showDetails ? 400 : 300}>
        <ResponsiveLine
          data={productivityData}
          margin={{ top: 20, right: 40, bottom: 60, left: 60 }}
          xScale={{ type: 'point' }}
          yScale={{
            type: 'linear',
            min: 50,
            max: 100,
            stacked: false,
          }}
          curve="monotoneX"
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: -45,
            legend: t('Time (hours)'),
            legendOffset: 45,
            legendPosition: 'middle',
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: t('Productivity %'),
            legendOffset: -45,
            legendPosition: 'middle',
          }}
          enableGridX={false}
          colors={{ scheme: 'accent' }}
          lineWidth={3}
          pointSize={10}
          pointColor={{ theme: 'background' }}
          pointBorderWidth={2}
          pointBorderColor={{ from: 'serieColor' }}
          pointLabelYOffset={-12}
          enableArea={true}
          areaOpacity={0.15}
          useMesh={true}
          legends={[
            {
              anchor: 'top-right',
              direction: 'column',
              justify: false,
              translateX: 0,
              translateY: 0,
              itemsSpacing: 0,
              itemDirection: 'left-to-right',
              itemWidth: 80,
              itemHeight: 20,
              itemOpacity: 0.75,
              symbolSize: 12,
              symbolShape: 'circle',
              symbolBorderColor: 'rgba(0, 0, 0, .5)',
              effects: [
                {
                  on: 'hover',
                  style: {
                    itemBackground: 'rgba(0, 0, 0, .03)',
                    itemOpacity: 1,
                  },
                },
              ],
            },
          ]}
          theme={{
            axis: {
              ticks: {
                text: {
                  fill: '#cccccc',
                },
              },
              legend: {
                text: {
                  fill: '#cccccc',
                },
              },
            },
            grid: {
              line: {
                stroke: '#555555',
                strokeWidth: 1,
              },
            },
            legends: {
              text: {
                fill: '#cccccc',
              },
            },
            tooltip: {
              container: {
                background: '#333333',
                color: '#ffffff',
              },
            },
          }}
        />
      </ContainerChart>

      {showDetails && (
        <div className="mt-4 overflow-hidden rounded">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-app-panel-dark text-white">
                <th className="border-b p-2 text-left">{t('Time')}</th>
                <th className="border-b p-2 text-left">{t('Productivity')}</th>
                <th className="border-b p-2 text-left">{t('Distance')}</th>
                <th className="border-b p-2 text-left">{t('Weight')}</th>
                <th className="border-b p-2 text-left">{t('Temperature')}</th>
                <th className="border-b p-2 text-left">{t('Rest Needed')}</th>
              </tr>
            </thead>
            <tbody>
              {fatigueData.map((item, index) => (
                <tr
                  key={index}
                  className={classNames(
                    'border-b',
                    item.restNeeded ? 'bg-red-900/20' : 'bg-app-panel-dark/50',
                  )}
                >
                  <td className="p-2">{item.time}</td>
                  <td className="p-2">
                    {fatigueMetrics[index].productivityTrend}%
                  </td>
                  <td className="p-2">{item.distance}m</td>
                  <td className="p-2">{item.weight}kg</td>
                  <td className="p-2">{item.temperature}°C</td>
                  <td className="p-2">
                    {item.restNeeded ? t('Yes') : t('No')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const WarehouseDashboardLabour: React.FC<WarehouseDashboardLabourProps> = ({
  laborMetrics,
  productivityMetrics,
  fatigueMetrics,
  timeRange,
}) => {
  const { t } = useTranslation('app');
  const currentMetrics = laborMetrics[laborMetrics.length - 1];
  const [showDetails, setShowDetails] = useState(true);
  const [showTimeAnalysis, setShowTimeAnalysis] = useState(true);
  const [showProductivityComparison, setShowProductivityComparison] =
    useState(true);
  const [aggregationType, setAggregationType] = useState('hourly');

  const aggregationOptions = [
    { id: 'hourly', label: t('Hourly') },
    { id: 'daily', label: t('Daily') },
    { id: 'weekly', label: t('Weekly') },
  ];

  return (
    <div className="p-4">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-2xl font-bold">{t('Labour Dashboard')}</h2>
        <span className="text-gray-400">{timeRange}</span>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        <ShiftUtilizationCard metrics={currentMetrics} />
        <FTETrackingCard metrics={currentMetrics} />
        <ProductivityCard metrics={productivityMetrics} />
      </div>

      <div className="mb-6 mt-6">
        <Stat
          hasHelper
          isPreview
          title={t('Productivity Comparison')}
          switchUnitOfMeasure={
            <header className="flex w-full flex-col items-center md:flex-row">
              <div className="text-xs uppercase">{t('Productivity')}</div>
              <div className="flex flex-1 items-center py-1 pt-2 lg:justify-end xl:p-0">
                <ButtonSwitchMulti
                  buttonType="minimal"
                  autoSize
                  className="mx-2"
                  selectedIndex={showProductivityComparison ? 0 : 1}
                  onClick={index => setShowProductivityComparison(index === 0)}
                  options={[{ label: t('Show') }, { label: t('Hide') }]}
                />
              </div>
            </header>
          }
        >
          {showProductivityComparison && (
            <ProductivityBarChart
              laborMetrics={laborMetrics}
              productivityMetrics={productivityMetrics}
            />
          )}
        </Stat>
      </div>

      <div className="mt-6">
        <Stat
          hasHelper
          isPreview
          title={t('Workforce Time Analysis')}
          switchUnitOfMeasure={
            <header className="flex w-full flex-col items-center md:flex-row">
              <div className="text-xs uppercase">{t('Time Analysis')}</div>
              <div className="flex flex-1 items-center py-1 pt-2 lg:justify-end xl:p-0">
                <ButtonSwitchMulti
                  buttonType="minimal"
                  autoSize
                  className="mx-2"
                  selectedIndex={showDetails ? 0 : 1}
                  onClick={index => setShowDetails(index === 0)}
                  options={[{ label: t('Details') }, { label: t('Off') }]}
                />
                <ButtonSwitchMulti
                  buttonType="minimal"
                  autoSize
                  className="mx-2"
                  selectedIndex={showTimeAnalysis ? 0 : 1}
                  onClick={index => setShowTimeAnalysis(index === 0)}
                  options={[{ label: t('Chart') }, { label: t('Off') }]}
                />
                <DropdownSelector
                  buttonTransparent
                  panelMode
                  className="rounded-full px-2"
                  classNameValue="text-xs"
                  value={aggregationType}
                  values={aggregationOptions.map(opt => opt.id)}
                  renderValue={v =>
                    aggregationOptions.find(opt => opt.id === v)?.label
                  }
                  onChange={v => setAggregationType(v)}
                />
              </div>
            </header>
          }
        >
          {showTimeAnalysis && (
            <WorkforceTimeAnalysis
              fatigueMetrics={fatigueMetrics}
              showDetails={showDetails}
            />
          )}
        </Stat>
      </div>
    </div>
  );
};

export default WarehouseDashboardLabour;
